<template>
	<div class="filter-container">
		<h3 class="heading" :data-test="type">
			{{ type }}
		</h3>
		<FallBack
			v-if="filterError"
			data-test="show-error"
			iconStyle="fal"
			icon="exclamation-triangle"
			message="We could not load this filter, please try again later."
		/>
		<div v-else class="button-container">
			<FilterButton
				class="filter-button"
				v-for="(option, index) in options"
				:key="index"
				:name="option.desc"
				:aria-label="`Filter by ${option.desc}`"
				:selected="isSelected(option.id)"
				@onClick="handleFilterButtonClick(option.id)"
			/>
		</div>
	</div>
</template>

<script>
import { debounce } from 'lodash';

import FilterButton from '@/components/global/FilterButton.vue';
import FallBack from '@/components/global/FallBack.vue';

export default {
	name: 'SingleFilter',
	props: {
		type: {
			// filter type, e.g. 'Contact Type'
			type: String,
			required: true,
		},
		options: {
			// filter options { desc: 'Borower', id: 1 }...
			type: Array,
			required: true,
		},
		selectedFilters: {
			// currently selected filters
			type: Array,
			required: true,
		},
		toggleFilter: {
			type: Boolean,
			default: false,
		},
		debounced: {
			// will debounce happen in this component?
			type: Boolean,
			default: false,
		},
		/**
		 * this enables the filters to display as not selected as soon
		 * as the reset button is clicked, otherwise they will not show as
		 * reset until after the debounce. Only applies when the
		 * MultipleFilters component is used
		 */
		filterReset: Boolean,
	},
	components: {
		FilterButton,
		FallBack,
	},
	data() {
		return {
			newFilter: [], // tracks last clicked filter
			localSelectedFilters: [], // tracks all set filters
		};
	},
	computed: {
		filterError() {
			return this.options.length === 0;
		},
	},
	methods: {
		isSelected(id) {
			return this.localSelectedFilters.includes(id);
		},
		handleFilterButtonClick(id) {
			this.newFilter = [id];

			if (this.toggleFilter) {
				this.localSelectedFilters = [id];
			} else {
				if (this.localSelectedFilters.includes(id)) {
					this.localSelectedFilters =
						this.localSelectedFilters.filter((el) => el !== id);
				} else {
					this.localSelectedFilters = [
						...this.localSelectedFilters,
						id,
					];
				}
			}
			if (this.debounced) {
				this.emitFilterChangeDebounced();
			} else {
				this.emitFilterChange();
			}
			if (this.filterReset) {
				this.$emit('clearReset');
			}
		},
		emitFilterChange() {
			this.$emit('onFilterChange', {
				type: this.type,
				filter: [...this.newFilter],
				toggleFilter: this.toggleFilter,
			});
			this.newFilter = [];
		},
		emitFilterChangeDebounced: debounce(function () {
			this.$emit('onFilterChange', {
				type: this.type,
				filter: [...this.localSelectedFilters],
				toggleFilter: this.toggleFilter,
			});
			this.newFilter = [];
		}, 1000),
	},
	watch: {
		filterReset: function (newVal) {
			if (newVal) {
				this.localSelectedFilters = [];
			}
		},
	},
	mounted() {
		this.localSelectedFilters = [...this.selectedFilters];
	},
};
</script>

<style scoped lang="scss">
.filter-container {
	box-sizing: border-box;
	width: 100%;
	z-index: 2;
	opacity: 1;

	.button-container {
		display: flex;
		flex-wrap: wrap;
		@include rem('gap', 10px);
		@include rem('margin-top', 10px);

		.filter-button {
			@include rem('font-size', 15px);
			@include rem('padding', 11px 8px);
			font-weight: 500;
			color: $brand-blue;
			flex-grow: 1;
		}
	}
}

.heading {
	margin: 0;
	@include rem('font-size', 16px);
}
</style>
